import React, { useCallback, useEffect, useState } from 'react';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tooltip
} from '@mui/material';
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import deleteIcon from '@iconify/icons-mdi/trash-can-outline';
import editIcon from '@iconify/icons-mdi/square-edit-outline';
import { getData, postData, putData, deleteData } from '../../../webService/webservice.js';
import { useStore } from "../../../Context/context.jsx";
import StoreMultiSelect from "../../Challange/storeMultiSelect.jsx";
import StoreSingleAllStoresSelect, { ALL_STORE_OPTION } from "../../Challange/StoreSingleAllStoresSelect.jsx";
import Footer from "../Footer/Footer-page.jsx";
import '../../../styles/Auth/expense.css';

// Cash taken out of the drawer at EOD. These entries only reduce the
// Closing Cash Balance; they are not expenses and do not touch sales or P&L.
const TYPE_OPTIONS = [
    { value: "BANK_TRANSFER", label: "Bank Transfer" },
    { value: "SELF_WITHDRAWAL", label: "Self Withdrawal" }
];
const typeLabel = (v) => TYPE_OPTIONS.find(o => o.value === v)?.label || v || "—";

const ADMIN_ROLES = ["SuperAdmin", "SelfAdmin", "Manager", "Accountant"];
const FILTERS = ["all", "daily", "weekly", "monthly", "yearly", "custom"];
const HEADINGS = [
    { label: "S.NO" },
    { label: "Store" },
    { label: "Transaction Type", sortKey: "transactionType" },
    { label: "Date", sortKey: "date" },
    { label: "Amount", sortKey: "amount" },
    { label: "Reference No" },
    { label: "Remarks" },
    { label: "Action" }
];

const today = () => DateTime.now().toISODate();
const money = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const CashWithdrawal = () => {
    const role = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");
    const initialStoreId = adminStoreIds[0] || "";
    const sessionToken = localStorage.getItem("Token");
    const isAdmin = ADMIN_ROLES.includes(role);
    const { selectedStoreId } = useStore();

    // List state
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({ bankTransfer: 0, selfWithdrawal: 0, total: 0 });
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(5);
    const [filter, setFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [sortField, setSortField] = useState("date");
    const [sortOrder, setSortOrder] = useState(-1);
    const [store, setStore] = useState(ALL_STORE_OPTION);
    const [loading, setLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    // Form state
    const emptyForm = {
        storeId: initialStoreId,
        storeName: "",
        transactionType: "",
        amount: "",
        date: today(),
        referenceNo: "",
        remarks: ""
    };
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editing, setEditing] = useState(null); // record being edited
    const [formData, setFormData] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [availableCash, setAvailableCash] = useState(null);
    const [loadingCash, setLoadingCash] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const listStoreId = isAdmin
        ? store?.value
        : (Array.isArray(selectedStoreId) ? selectedStoreId[0] : selectedStoreId);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: limit === "all" ? 999999 : limit,
                filter,
                sortField,
                sortOrder
            };
            if (filter === "custom") {
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;
            }
            if (typeFilter) params.transactionType = typeFilter;
            if (listStoreId && listStoreId.length === 24) params.storeId = listStoreId;

            const res = await getData("cashwithdrawal/withdrawals", params, sessionToken);
            if (res?.status !== 200) {
                toast.error(res?.message || "Failed to fetch withdrawals");
                return;
            }
            setRows(res.data?.data || []);
            setTotals(res.data?.totals || { bankTransfer: 0, selfWithdrawal: 0, total: 0 });
            setTotalCount(res.data?.totalCount || 0);
            setTotalPages(Math.max(res.data?.totalPages || 1, 1));
        } catch (err) {
            toast.error(err.message || "Failed to fetch withdrawals");
        } finally {
            setLoading(false);
        }
    }, [currentPage, limit, filter, sortField, sortOrder, startDate, endDate, typeFilter, listStoreId, sessionToken]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows, refreshKey]);

    // Back to page 1 whenever the filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [limit, filter, startDate, endDate, typeFilter, listStoreId, sortField, sortOrder]);

    // Store name for the form's store picker
    useEffect(() => {
        if (!isFormOpen || !formData.storeId || formData.storeName) return;
        getData(`stores/store/${formData.storeId}`, {}, sessionToken)
            .then(res => {
                if (res?.data?.storeName) {
                    setFormData(prev => ({ ...prev, storeName: res.data.storeName }));
                }
            })
            .catch(() => { });
    }, [isFormOpen, formData.storeId, formData.storeName, sessionToken]);

    // Closing cash available for the chosen store + date
    useEffect(() => {
        if (!isFormOpen || !formData.storeId || !formData.date) {
            setAvailableCash(null);
            setLoadingCash(false);
            return;
        }
        let cancelled = false;
        setLoadingCash(true);
        getData("closingbalance/closing", { storeId: formData.storeId, date: formData.date }, sessionToken)
            .then(res => {
                if (cancelled) return;
                let cash = res?.data?.totals?.closingCash;
                if (typeof cash !== "number") {
                    setAvailableCash(null);
                    return;
                }
                // When editing, the record's own amount is already deducted in closingCash
                const editStoreId = editing?.storeId?._id || editing?.storeId;
                if (
                    editing &&
                    String(editStoreId) === String(formData.storeId) &&
                    DateTime.fromISO(editing.date).toISODate() === formData.date
                ) {
                    cash += Number(editing.amount || 0);
                }
                setAvailableCash(cash);
            })
            .catch(() => !cancelled && setAvailableCash(null))
            .finally(() => !cancelled && setLoadingCash(false));
        return () => { cancelled = true; };
    }, [isFormOpen, formData.storeId, formData.date, editing, sessionToken]);

    const loadStores = async (inputValue, loadedOptions, { page }) => {
        try {
            const res = await getData("stores/stores", { page, limit: 10, search: inputValue }, sessionToken);
            const results = res?.data?.results || [];
            return {
                options: results.map(s => ({ value: s._id, label: s.storeName })),
                hasMore: results.length === 10,
                additional: { page: page + 1 }
            };
        } catch {
            return { options: [], hasMore: false };
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 1 ? -1 : 1);
        } else {
            setSortField(field);
            setSortOrder(-1);
        }
    };

    const handleDateRange = (type, value) => {
        if (type === "start") {
            if (value && endDate && value > endDate) return toast.error("Start date cannot be after end date");
            setStartDate(value);
        } else {
            if (value && startDate && value < startDate) return toast.error("End date cannot be before start date");
            setEndDate(value);
        }
    };

    const openAdd = () => {
        setEditing(null);
        setFormData(emptyForm);
        setErrors({});
        setIsFormOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        const storeIdVal = row.storeId?._id || row.storeId;
        setFormData({
            storeId: storeIdVal,
            storeName: row.store?.storeName || "",
            transactionType: row.transactionType,
            amount: String(row.amount ?? ""),
            date: DateTime.fromISO(row.date).toISODate(),
            referenceNo: row.referenceNo || "",
            remarks: row.remarks || ""
        });
        setErrors({});
        setIsFormOpen(true);
    };

    const closeForm = () => {
        setIsFormOpen(false);
        setEditing(null);
        setErrors({});
        setAvailableCash(null);
        setLoadingCash(false);
    };

    const getAmountValidationMessage = (amountValue, cashVal) => {
        if (amountValue === "" || amountValue === undefined || amountValue === null) {
            if (cashVal !== null && cashVal <= 0) {
                return "Insufficient cash balance. Maximum withdrawal allowed is ₹0";
            }
            return null;
        }
        const amt = Number(amountValue);
        if (isNaN(amt) || amt <= 0) {
            return "Amount must be greater than 0";
        }
        if (cashVal !== null) {
            const max = Math.max(0, cashVal);
            if (cashVal <= 0 || amt > cashVal + 0.001) {
                return `Insufficient cash balance. Maximum withdrawal allowed is ₹${money(max)}`;
            }
        }
        return null;
    };

    const currentAmountError = getAmountValidationMessage(formData.amount, availableCash);
    const isAmountValid = formData.amount !== "" && !currentAmountError && Number(formData.amount) > 0;
    const isSubmitDisabled = saving ||
        loadingCash ||
        !formData.storeId ||
        !formData.transactionType ||
        !formData.date ||
        !isAmountValid;

    const validate = () => {
        const e = {};
        if (!formData.storeId) e.storeId = "Store is required";
        if (!formData.transactionType) e.transactionType = "Transaction type is required";
        if (!formData.date) e.date = "Date is required";
        else if (formData.date > today()) e.date = "Date cannot be in the future";

        const amtErr = getAmountValidationMessage(formData.amount, availableCash);
        if (!formData.amount) {
            e.amount = "Amount is required";
        } else if (amtErr) {
            e.amount = amtErr;
        }
        return e;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length || isSubmitDisabled) return;

        const payload = {
            storeId: formData.storeId,
            transactionType: formData.transactionType,
            amount: Number(formData.amount),
            date: formData.date,
            referenceNo: formData.referenceNo.trim(),
            remarks: formData.remarks.trim()
        };

        setSaving(true);
        try {
            const res = editing
                ? await putData(`cashwithdrawal/withdrawal/${editing._id}`, payload, sessionToken)
                : await postData("cashwithdrawal/", payload, sessionToken);

            if (res?.status !== 200) {
                const msg = Array.isArray(res?.message) ? res.message.join(", ") : res?.message;
                toast.error(msg || "Failed to save withdrawal");
                return;
            }
            toast.success(res.message || "Saved");
            closeForm();
            setRefreshKey(k => k + 1);
        } catch (err) {
            toast.error(err.message || "Failed to save withdrawal");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            const res = await deleteData(`cashwithdrawal/withdrawal/${deleteId}`, sessionToken);
            if (res?.status !== 200) {
                toast.error(res?.message || "Failed to delete withdrawal");
                return;
            }
            toast.success(res.message || "Deleted");
            setRefreshKey(k => k + 1);
        } catch (err) {
            toast.error(err.message || "Failed to delete withdrawal");
        } finally {
            setDeleteId(null);
        }
    };

    const handlePageChange = (dir) => {
        if (dir === "first") setCurrentPage(1);
        if (dir === "prev") setCurrentPage(p => Math.max(p - 1, 1));
        if (dir === "next") setCurrentPage(p => Math.min(p + 1, totalPages));
        if (dir === "last") setCurrentPage(totalPages);
    };

    const cellStyle = { color: '#525252', fontSize: '12px', fontWeight: 600, textAlign: 'center' };
    const canDelete = role !== "SelfAdmin" && role !== "Accountant";
    const pageStart = totalCount === 0 ? 0 : (currentPage - 1) * (limit === "all" ? 0 : limit) + 1;

    return (
        <div>
            {/* Header: title, store, filters */}
            <div className='Expense-Eidgets'>
                <div className='Expense'>
                    <Typography variant="h6" sx={{ mb: 0, fontWeight: 700, color: '#3C0008' }}>
                        Bank Transfer / Self Withdrawal
                    </Typography>
                    {isAdmin && (
                        <div style={{ minWidth: 220, flex: '0 1 auto' }}>
                            <StoreSingleAllStoresSelect value={store} onChange={setStore} />
                        </div>
                    )}
                </div>

                <div className="Expense-header-actions">
                    {FILTERS.map(f => (
                        <button
                            key={f}
                            className={`filter-btn ${filter === f ? "active" : ""}`}
                            onClick={() => {
                                setFilter(f);
                                if (f !== "custom") {
                                    setStartDate("");
                                    setEndDate("");
                                }
                            }}
                        >
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                    ))}
                </div>

                {filter === "custom" && (
                    <div className="custom-date-container">
                        <div className="date-input-group">
                            <span>From</span>
                            <input type="date" className="expense-date-input" value={startDate}
                                onChange={(e) => handleDateRange("start", e.target.value)} />
                        </div>
                        <div className="date-input-group">
                            <span>To</span>
                            <input type="date" className="expense-date-input" value={endDate}
                                onChange={(e) => handleDateRange("end", e.target.value)} />
                        </div>
                    </div>
                )}
            </div>

            {/* Summary cards */}
            <div className="Expense-cards">
                <div className="Expense-card card-expense">
                    <div className="expense-card-left">
                        <Icon icon="mdi:cash-minus" style={{ fontSize: 30, color: '#fff' }} />
                    </div>
                    <div className="expense-card-right">
                        <h3>Total Withdrawn</h3>
                        <p>₹{money(totals.total)}</p>
                    </div>
                </div>
                <div className="Expense-card card-purchase">
                    <div className="expense-card-left">
                        <Icon icon="mdi:bank-transfer-out" style={{ fontSize: 30, color: '#fff' }} />
                    </div>
                    <div className="expense-card-right">
                        <h3>Bank Transfer</h3>
                        <p>₹{money(totals.bankTransfer)}</p>
                    </div>
                </div>
                <div className="Expense-card card-return">
                    <div className="expense-card-left">
                        <Icon icon="mdi:account-cash-outline" style={{ fontSize: 30, color: '#3C0008' }} />
                    </div>
                    <div className="expense-card-right">
                        <h3>Self Withdrawal</h3>
                        <p>₹{money(totals.selfWithdrawal)}</p>
                    </div>
                </div>
            </div>

            <Typography sx={{ mt: 2, fontSize: 13, color: '#64748b' }}>
                These entries reduce only the Closing Cash Balance. The remaining balance carries forward as the next day's Opening Cash.
                They are not counted as expenses and do not affect sales or profit/loss.
            </Typography>

            {/* Toolbar */}
            <div className='Expense-widgets' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
                <select
                    className="entries-select"
                    style={{ minWidth: 180, padding: '8px 10px' }}
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                >
                    <option value="">All Transaction Types</option>
                    {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <div className="add-Expense-buttons">
                    <button className="add-Product-button" onClick={openAdd}>+ Add Withdrawal</button>
                </div>
            </div>

            {/* Table */}
            <Box component={Paper} sx={{ mt: 2 }}>
                <TableContainer component={Paper} sx={{ maxHeight: 400, overflowY: "auto" }}>
                    <Table className="custom-product-table" sx={{ '& td, & th': { border: 'none' } }}>
                        <TableHead className="custom-product-head">
                            <TableRow>
                                {HEADINGS.map(h => (
                                    <TableCell key={h.label} sx={{ padding: "12px 16px", textAlign: "center", position: "sticky", top: 0, zIndex: 0, backgroundColor: '#3C0008' }}>
                                        <Typography variant="body2" sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", color: "#fff", fontSize: "14px", fontWeight: 600 }}>
                                            {h.label}
                                            {h.sortKey && (
                                                <span onClick={() => handleSort(h.sortKey)} style={{ fontSize: "10px", cursor: "pointer", marginLeft: 4, userSelect: "none" }}>
                                                    {sortField === h.sortKey ? (sortOrder === 1 ? "▲" : "▼") : "▲▼"}
                                                </span>
                                            )}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={HEADINGS.length}>
                                        <Typography sx={{ textAlign: 'center', padding: '20px', fontWeight: 500 }}>
                                            {loading ? "Loading..." : "No withdrawals found."}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : rows.map((row, i) => (
                                <TableRow key={row._id}>
                                    <TableCell><Typography sx={cellStyle}>{pageStart + i}</Typography></TableCell>
                                    <TableCell><Typography sx={cellStyle}>{row.store?.storeName || "—"}</Typography></TableCell>
                                    <TableCell>
                                        <Typography sx={{
                                            ...cellStyle,
                                            display: 'inline-block',
                                            px: 1.5, py: 0.5,
                                            borderRadius: '12px',
                                            backgroundColor: row.transactionType === "BANK_TRANSFER" ? '#e0f2fe' : '#fef3c7',
                                            color: row.transactionType === "BANK_TRANSFER" ? '#0369a1' : '#92400e'
                                        }}>
                                            {typeLabel(row.transactionType)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell><Typography sx={cellStyle}>{row.date ? DateTime.fromISO(row.date).toFormat("dd/MM/yyyy") : "—"}</Typography></TableCell>
                                    <TableCell><Typography sx={cellStyle}>₹{money(row.amount)}</Typography></TableCell>
                                    <TableCell><Typography sx={cellStyle}>{row.referenceNo || "—"}</Typography></TableCell>
                                    <TableCell><Typography sx={cellStyle}>{row.remarks || "—"}</Typography></TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'center' }}>
                                            <Tooltip title="Edit">
                                                <Icon icon={editIcon} width={20} height={20} style={{ cursor: "pointer" }} onClick={() => openEdit(row)} />
                                            </Tooltip>
                                            {canDelete && (
                                                <Tooltip title="Delete">
                                                    <Icon icon={deleteIcon} width={20} height={20} style={{ color: '#C10000', cursor: "pointer" }} onClick={() => setDeleteId(row._id)} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <div className="product-bottom">
                    <div className="Expense-section show-entries">
                        <span className="text-label">Show</span>
                        <select className="entries-select" value={limit}
                            onChange={(e) => setLimit(e.target.value === "all" ? "all" : Number(e.target.value))}>
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="all">All</option>
                        </select>
                        <span className="text-label">Entries</span>
                    </div>
                    <div className="Expense-section showing-range">
                        {limit === 'all'
                            ? `Showing all ${totalCount} entries`
                            : `Showing ${pageStart} to ${Math.min(currentPage * limit, totalCount)} of ${totalCount} entries`}
                    </div>
                    <div className="Expense-section spacer"></div>
                    <div className="Expense-section pagination-buttons">
                        <button className="pagination-btn" disabled={currentPage === 1} onClick={() => handlePageChange("first")}><Icon icon='mdi:chevron-double-left' /></button>
                        <button className="pagination-btn" disabled={currentPage === 1} onClick={() => handlePageChange("prev")}><Icon icon='mdi:chevron-left' /></button>
                        <button className="pagination-btn" disabled={currentPage >= totalPages} onClick={() => handlePageChange("next")}><Icon icon='mdi:chevron-right' /></button>
                        <button className="pagination-btn" disabled={currentPage >= totalPages} onClick={() => handlePageChange("last")}><Icon icon='mdi:chevron-double-right' /></button>
                    </div>
                </div>
            </Box>

            {/* Add / Edit dialog */}
            <Dialog open={isFormOpen} onClose={saving ? undefined : closeForm} fullWidth maxWidth="sm">
                <form onSubmit={handleSubmit}>
                    <DialogTitle sx={{ fontWeight: 700, color: '#3C0008' }}>
                        {editing ? "Edit Withdrawal" : "Add Withdrawal"}
                    </DialogTitle>
                    <DialogContent dividers>
                        <div className="Expense-form-group" style={{ marginBottom: 14 }}>
                            <label className="input-label">Store Name</label>
                            <StoreMultiSelect
                                value={formData.storeId ? [{ value: formData.storeId, label: formData.storeName || "Loading..." }] : []}
                                onChange={(val) => {
                                    const picked = val?.[val.length - 1];
                                    setFormData(prev => ({ ...prev, storeId: picked?.value || "", storeName: picked?.label || "" }));
                                }}
                                loadStores={loadStores}
                                loggedInRole={role}
                                assignedStoreIds={adminStoreIds}
                                isDisabled={!!editing || adminStoreIds.length === 1}
                            />
                            {errors.storeId && <p className="field-error">{errors.storeId}</p>}
                        </div>

                        <div className="Expense-form-group" style={{ marginBottom: 14 }}>
                            <label className="input-label">Transaction Type</label>
                            <select
                                className="Expense-input"
                                value={formData.transactionType}
                                onChange={(e) => setFormData(prev => ({ ...prev, transactionType: e.target.value }))}
                            >
                                <option value="">Select Transaction Type</option>
                                {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            {errors.transactionType && <p className="field-error">{errors.transactionType}</p>}
                        </div>

                        <div className="Expense-form-group" style={{ marginBottom: 14 }}>
                            <label className="input-label">Date</label>
                            <input
                                type="date"
                                className="Expense-input"
                                max={today()}
                                value={formData.date}
                                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                            />
                            {errors.date && <p className="field-error">{errors.date}</p>}
                        </div>

                        <div className="Expense-form-group" style={{ marginBottom: 14 }}>
                            <label className="input-label">Amount (₹) *</label>
                            <input
                                className="Expense-input"
                                inputMode="decimal"
                                placeholder="0.00"
                                value={formData.amount}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (/^\d*\.?\d{0,2}$/.test(v)) {
                                        setFormData(prev => ({ ...prev, amount: v }));
                                        if (errors.amount) {
                                            setErrors(prev => ({ ...prev, amount: "" }));
                                        }
                                    }
                                }}
                            />
                            {loadingCash ? (
                                <Typography sx={{ fontSize: 12, mt: 0.5, color: '#64748b' }}>
                                    Fetching closing cash balance...
                                </Typography>
                            ) : availableCash !== null ? (
                                <Box sx={{
                                    mt: 1,
                                    p: 1.25,
                                    borderRadius: '8px',
                                    backgroundColor: currentAmountError ? '#fef2f2' : '#f0fdf4',
                                    border: `1px solid ${currentAmountError ? '#fecaca' : '#bbf7d0'}`,
                                    transition: 'all 0.2s ease-in-out'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px' }}>
                                        <span style={{ color: '#475569', fontWeight: 600 }}>Closing Cash Balance:</span>
                                        <strong style={{ color: availableCash <= 0 ? '#dc2626' : '#15803d' }}>₹{money(availableCash)}</strong>
                                    </div>

                                    {Number(formData.amount) > 0 && !currentAmountError && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', marginTop: 4 }}>
                                            <span style={{ color: '#475569' }}>Remaining After Withdrawal:</span>
                                            <strong style={{ color: '#047857' }}>₹{money(availableCash - Number(formData.amount))}</strong>
                                        </div>
                                    )}

                                    {currentAmountError && (
                                        <div style={{ color: '#b91c1c', fontSize: '12.5px', fontWeight: 600, marginTop: 4 }}>
                                            ⚠️ {currentAmountError}
                                        </div>
                                    )}
                                </Box>
                            ) : null}
                            {errors.amount && !currentAmountError && <p className="field-error">{errors.amount}</p>}
                        </div>

                        <div className="Expense-form-group" style={{ marginBottom: 14 }}>
                            <label className="input-label">Reference No (optional)</label>
                            <input
                                className="Expense-input"
                                placeholder="Bank reference / UTR"
                                value={formData.referenceNo}
                                onChange={(e) => setFormData(prev => ({ ...prev, referenceNo: e.target.value }))}
                            />
                        </div>

                        <div className="Expense-form-group">
                            <label className="input-label">Remarks (optional)</label>
                            <textarea
                                className="Expense-input"
                                style={{ height: 80 }}
                                value={formData.remarks}
                                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                            />
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <button type="button" className="cancel-Product-button" onClick={closeForm} disabled={saving}>Cancel</button>
                        <button
                            type="submit"
                            className="add-Product-button"
                            disabled={isSubmitDisabled}
                            style={isSubmitDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                        >
                            {saving ? "Saving..." : editing ? "Update" : "Add Withdrawal"}
                        </button>
                    </DialogActions>
                </form>
            </Dialog>

            <Footer />

            {/* Delete confirm */}
            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
                <DialogTitle>Delete Withdrawal</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this entry? The amount will be added back to the closing cash balance.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteId(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CashWithdrawal;
